import React, { memo } from 'react';
import { Skeleton } from '@mui/material';
import Tooltip from '@mui/material/Tooltip';

/* ── Per-card theme config ──────────────────────────────────────────────── */
const THEMES = [
  { bg: '#ECFDF5', border: '#d1fae5', lineColor: '#22c55e', iconColor: '#16a34a', arrowUp: true,  },
  { bg: '#ECFDF5', border: '#d1fae5', lineColor: '#ef4444', iconColor: '#dc2626', arrowUp: false, },
  { bg: '#ECFDF5', border: '#d1fae5', lineColor: '#22c55e', iconColor: '#16a34a', arrowUp: true,  },
  { bg: '#ECFDF5', border: '#d1fae5', lineColor: '#ef4444', iconColor: '#dc2626', arrowUp: false, },
  { bg: '#ECFDF5', border: '#d1fae5', lineColor: '#22c55e', iconColor: '#16a34a', arrowUp: true,  },
  { bg: '#ECFDF5', border: '#d1fae5', lineColor: '#22c55e', iconColor: '#16a34a', arrowUp: true,  },
  { bg: '#ECFDF5', border: '#d1fae5', lineColor: '#22c55e', iconColor: '#16a34a', arrowUp: true,  },
];

/* ── Trend arrow icon (top-right circle) ──────────────────────────────── */
const TrendIcon = ({ arrowUp, iconColor }) => (
  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, width: 18, height: 18 }}>
    <svg width="18" height="18" viewBox="0 0 18 18" fill="none" shapeRendering="geometricPrecision">
      {arrowUp ? (
        <>
          <polyline
            points="3,12 7,8 10,11 15,6"
            fill="none"
            stroke={iconColor}
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
          <polygon
            points="15,6 12.6,5.2 13.6,7.2"
            fill={iconColor}
          />
        </>
      ) : (
        <>
          <polyline
            points="3,6 7,10 10,7 15,12"
            fill="none"
            stroke={iconColor}
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
          <polygon
            points="15,12 13.6,10.2 12.6,12.8"
            fill={iconColor}
          />
        </>
      )}
    </svg>
  </div>
);

/* ── Sparkline SVG wave ────────────────────────────────────────────────── */
const Sparkline = ({ color }) => {
  /* Generates a gentle natural-looking wave */
    const pts = "0,18 14,14 28,16 42,10 56,13 70,9 84,12 98,7 112,10 126,8 140,11";   return (
    <svg
      width="80%" height="28"
      viewBox="0 0 140 28"
      preserveAspectRatio="none"
      style={{ marginLeft:'15px'}}
    >
      <polyline 
        points={pts}
        fill="none"
        stroke={color}
        strokeWidth="1.8"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
};

/* ── Main component ───────────────────────────────────────────────────── */
const Reportcard = ({
  reportImg,
  title,
  value,
  loadingState,
  size,
  customClass,
  grade,
  index = 0,
}) => {
  const theme = THEMES[index % THEMES.length];

  const cardStyle = {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'space-between',
    background: theme.bg,
    borderRadius: '12px',
    border: `1px solid ${theme.border}`,
    padding: '1rem 1.1rem 0 1.1rem',
    minHeight: '120px',
    marginBottom: '1rem',
    overflow: 'hidden',
    width: size || '100%',
    boxSizing: 'border-box',
    boxShadow: 'none',
    transition: 'box-shadow 0.2s ease',
    cursor: 'inherit',
  };

  if (loadingState) {
    return (
      <div style={cardStyle}>
        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
          <Skeleton width="80px" height="16px" />
          <Skeleton variant="circular" width="34px" height="34px" />
        </div>
        <Skeleton width="130px" height="30px" style={{ marginBottom: 8 }} />
        <Skeleton width="100%" height="28px" />
      </div>
    );
  }

  return (
    <div style={cardStyle}>
      {/* ── Row 1: label + trend icon ── */}
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
        <span style={{
          fontSize: '16px',
          fontFamily: '"gilroyMedium", Inter, sans-serif',
          color: '#374151',
          lineHeight: 1.4,
          maxWidth: 'calc(100% - 44px)',
        }}>
          {title}
        </span>
        <TrendIcon arrowUp={theme.arrowUp} iconColor={theme.iconColor} />
      </div>

      {/* ── Row 2: bold value ── */}
      <Tooltip describeChild title={<span style={{ fontSize: '12px' }}>{value}</span>}>
        <div style={{
          fontSize: '22px',
          fontFamily: '"gilroyBold", Inter, sans-serif',
          color: '#111827',
          letterSpacing: '-0.5px',
          lineHeight: 1.2,
          margin: '0.4rem 0 0.3rem',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
        }}>
          {value}
        </div>
      </Tooltip>

      {/* ── Row 3: sparkline wave ── */}
      <div style={{ margin: '0 -1.1rem', lineHeight: 0 }}>
        <Sparkline color={theme.lineColor} />
      </div>

      {/* ── Row 4: subtext ── */}
      <div style={{
        fontSize: '11px',
        fontFamily: '"gilroyMedium", Inter, sans-serif',
        color: '#6b7280',
        paddingBottom: '0.6rem',
        paddingTop: '0.15rem',
      }}>
        {theme.sub}
      </div>
    </div>
  );
};

export default memo(Reportcard);
